import type { RpcMethod, RpcParams, ServerEventName, ServerEvents } from '@bccomms/shared';
import { apiBase, wsUrlFor } from './native';

type EventHandler<E extends ServerEventName> = (data: ServerEvents[E]) => void;
type Pending = { resolve: (v: unknown) => void; reject: (e: Error) => void; method: string };

export type ConnState = 'idle' | 'connecting' | 'open' | 'closed';

/**
 * WebSocket RPC client. One socket carries requests (id/method/params), responses and
 * unsolicited server events. Reconnection is left to the session layer so it can rebuild media.
 */
export class RpcClient {
  private ws: WebSocket | null = null;
  private nextId = 1;
  private readonly pending = new Map<number, Pending>();
  private readonly listeners = new Map<string, Set<(d: unknown) => void>>();
  state: ConnState = 'idle';
  onStateChange: ((s: ConnState) => void) | null = null;

  constructor(private readonly url: string) {}

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.setState('connecting');
      const ws = new WebSocket(this.url);
      this.ws = ws;
      ws.onopen = () => {
        this.setState('open');
        resolve();
      };
      ws.onerror = () => {
        if (this.state === 'connecting') reject(new Error('connection failed'));
      };
      ws.onclose = () => {
        this.setState('closed');
        for (const p of this.pending.values()) p.reject(new Error('connection closed'));
        this.pending.clear();
        if (this.ws === ws) this.ws = null;
      };
      ws.onmessage = (ev) => this.handle(ev.data as string);
    });
  }

  close(): void {
    const ws = this.ws;
    this.ws = null;
    ws?.close();
    this.setState('closed');
  }

  private setState(s: ConnState): void {
    if (this.state === s) return;
    this.state = s;
    this.onStateChange?.(s);
  }

  private handle(raw: string): void {
    let msg: { id?: number; ok?: boolean; result?: unknown; error?: string; event?: string; data?: unknown };
    try {
      msg = JSON.parse(raw);
    } catch {
      return;
    }
    if (typeof msg.id === 'number') {
      const p = this.pending.get(msg.id);
      if (!p) return;
      this.pending.delete(msg.id);
      if (msg.ok) p.resolve(msg.result);
      else p.reject(new Error(msg.error ?? `${p.method} failed`));
      return;
    }
    if (typeof msg.event === 'string') {
      for (const fn of this.listeners.get(msg.event) ?? []) {
        try {
          fn(msg.data);
        } catch (err) {
          console.error(`event handler ${msg.event} threw`, err);
        }
      }
    }
  }

  call<M extends RpcMethod>(method: M, params: RpcParams<M>): Promise<unknown> {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return Promise.reject(new Error('not connected'));
    const id = this.nextId++;
    return new Promise((resolve, reject) => {
      this.pending.set(id, { resolve, reject, method });
      ws.send(JSON.stringify({ id, method, params }));
    });
  }

  on<E extends ServerEventName>(event: E, fn: EventHandler<E>): () => void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(fn as (d: unknown) => void);
    return () => set?.delete(fn as (d: unknown) => void);
  }
}

export function wsUrl(): string {
  return wsUrlFor(apiBase());
}
